import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { ArrowLeft, Cookie, FileWarning } from "lucide-react";
import { openCookieSettings } from "@/lib/consent";
import { useDatosSeo } from "@/publico/datos";
import { useCabecera } from "@/publico/cabecera";
import { metaDe } from "@/publico/metaRuta";
import { PAGES } from "./legalTextos";

const MARCAS = { razon_social: "[Razón social]", nif: "[NIF/CIF]", direccion: "[Dirección fiscal]", email: "[Email de contacto]" };
// /legal/titular devuelve {nombre, nif, direccion_fiscal, email}; los huecos llegan como «[COMPLETAR: ...]».
const CAMPO_DE_MARCA = { razon_social: "nombre", nif: "nif", direccion: "direccion_fiscal", email: "email" };
const rellenar = (texto, datos) => Object.entries(MARCAS).reduce((t, [k, m]) => (datos[CAMPO_DE_MARCA[k]] ? t.split(m).join(datos[CAMPO_DE_MARCA[k]]) : t), texto);

export const LEGAL_LINKS = [
  ["cookies", "Cookies"], ["mas-informacion", "Más información"], ["privacidad", "Privacidad"], ["terminos", "Términos y condiciones"],
  ["encargado-tratamiento", "Encargado del tratamiento"], ["referidos", "Programa de referidos"], ["plan-pro", "Plan Pro"], ["aviso-legal", "Aviso legal"],
];

function Borrador() {
  return (
    <p className="mb-8 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="legal-borrador">
      <FileWarning className="mt-0.5 h-4 w-4 shrink-0" /> Borrador pendiente de revisión por la gestoría. Este texto puede cambiar.
    </p>
  );
}

export default function Legal() {
  const { slug } = useParams();
  const page = PAGES[slug] || PAGES.cookies;
  const [datos, setDatos] = useState(() => globalThis.__SEO__?.titular || {});
  const seo = useDatosSeo();
  useCabecera(metaDe(`/legal/${slug}`, seo));
  useEffect(() => { api.get("/legal/titular").then((r) => setDatos(r.data)).catch(() => {}); }, []);
  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-[#F0FDF8] to-[#E0F2FE]/50" data-testid={`legal-page-${slug}`}>
      <header className="sticky top-0 z-30 border-b border-sky-100 bg-white px-4 md:px-10 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="legal-home-link">
          <img src="/nuvlipro-logo.png" alt="NuvliPro" className="w-9 h-9 rounded-xl border border-sky-100 bg-white p-1 object-contain" />
          <span className="font-heading font-extrabold text-xl text-[#034694]">Nuvli<span className="text-[#0284C7]">Pro</span></span>
        </Link>
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-medium text-[#034694] hover:underline" data-testid="legal-back-link"><ArrowLeft className="w-4 h-4" /> Volver</Link>
      </header>
      <main className="max-w-3xl mx-auto px-6 py-14">
        <span className="inline-block px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[11px] font-bold uppercase tracking-wide mb-4">Información legal</span>
        <h1 className="font-heading text-4xl sm:text-5xl font-extrabold text-[#0A2540] mb-10" data-testid="legal-title">{page.title}</h1>
        {page.borrador && <Borrador />}
        <div className="space-y-8">
          {page.sections.map(([h, p, enlaces], i) => (
            <section key={h} className="border-l-4 border-emerald-400 pl-5">
              <h2 className="font-heading text-base md:text-lg font-bold text-[#0A2540] mb-1.5">{h}</h2>
              <p className="text-sm md:text-base text-slate-600 leading-relaxed whitespace-pre-line" data-testid={`legal-section-${i}`}>{rellenar(p, datos)}</p>
              {enlaces && (
                <p className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm">
                  {enlaces.map(([s, l]) => <Link key={s} to={`/legal/${s}`} className="font-semibold text-[#0284C7] hover:underline" data-testid={`legal-section-${i}-enlace-${s}`}>{l}</Link>)}
                </p>
              )}
            </section>
          ))}
        </div>
        <button onClick={openCookieSettings} className="mt-12 inline-flex items-center gap-2 h-11 px-6 rounded-full border-2 border-[#0A2540] text-[#0A2540] font-semibold hover:bg-white transition-colors" data-testid="legal-cookie-settings">
          <Cookie className="w-4 h-4" /> Configurar cookies
        </button>
        <nav className="mt-10 flex flex-wrap gap-x-5 gap-y-2 text-sm" data-testid="legal-nav">
          {LEGAL_LINKS.map(([s, l]) => (
            <Link key={s} to={`/legal/${s}`} className={s === slug ? "font-bold text-emerald-700" : "text-[#0284C7] hover:underline"} data-testid={`legal-nav-${s}`}>{l}</Link>
          ))}
        </nav>
      </main>
    </div>
  );
}
